import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faSearch, faSync, faEdit, faSave, faTimes,
    faBuilding, faStore, faBan, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Spinner } from 'react-bootstrap';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * SuperAdminStaff — Global view of all staff / users across all branches.
 * Allows quick editing (deactivation, password reset) from super admin context.
 */
const SuperAdminStaff = () => {
    const [staff, setStaff]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [search, setSearch]         = useState('');
    const [compFilter, setCompFilter] = useState('');
    const [companies, setCompanies]   = useState([]);
    const [modal, setModal]           = useState(null);
    const [saving, setSaving]         = useState(false);
    const [editData, setEditData]     = useState({});

    useEffect(() => { fetchStaff(); }, []);

    const fetchStaff = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/staff', {}, r => {
            if (r.success) {
                const data = r.data || [];
                setStaff(data);
                const uniq = {};
                data.forEach(s => { if (s.EmpresaId) uniq[s.EmpresaId] = s.EmpresaNombre; });
                setCompanies(Object.entries(uniq).map(([id, name]) => ({ id, name })));
            } else {
                Notify({ type:'fail', text:'Error al cargar personal' });
            }
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        return staff.filter(s => {
            const q = search.toLowerCase();
            const matchSearch = !q ||
                s.UsrNombre?.toLowerCase().includes(q) ||
                s.UsrApellido?.toLowerCase().includes(q) ||
                s.UsrDNI?.toString().includes(q) ||
                s.SucursalNombre?.toLowerCase().includes(q);
            const matchComp = !compFilter || String(s.EmpresaId) === String(compFilter);
            return matchSearch && matchComp;
        });
    }, [staff, search, compFilter]);

    const openEdit = (user) => {
        setEditData({ UsrActivo: user.UsrActivo, newPassword:'' });
        setModal(user);
    };
    const closeModal = () => { if (!saving) setModal(null); };

    const handleSave = () => {
        setSaving(true);
        request.Put(API_BASE + `superadmin/staff/${modal.UsrId}`, editData, r => {
            if (r.success) {
                Notify({ type:'success', text:'Usuario actualizado' });
                setStaff(prev => prev.map(s => s.UsrId === modal.UsrId
                    ? { ...s, UsrActivo: editData.UsrActivo }
                    : s
                ));
                closeModal();
            } else {
                Notify({ type:'fail', text: r.msg || 'Error al guardar cambios' });
            }
            setSaving(false);
        }, 'json', () => { setSaving(false); });
    };

    return (
        <div className="animate-in pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ fontWeight:800, margin:0 }}>
                        <FontAwesomeIcon icon={faUsers} style={{ color:'var(--sa-primary)', marginRight:'0.6rem' }} />
                        Usuarios / Personal
                    </h2>
                    <p className="text-muted small mt-1 mb-0">Vista global de todo el staff dado de alta en sucursales</p>
                </div>
                <button className="sa-btn sa-btn-ghost" onClick={fetchStaff} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> Actualizar
                </button>
            </div>

            {/* Filters */}
            <div className="d-flex gap-3 mb-4 flex-wrap">
                <div className="sa-search-bar" style={{ flex:'1', maxWidth:'none' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color:'#475569' }} />
                    <input
                        placeholder="Buscar por nombre, DNI o sucursal..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <span style={{ color:'#475569', fontSize:'0.75rem', whiteSpace:'nowrap' }}>
                            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <select
                    className="sa-input"
                    style={{ width:'auto', minWidth:200 }}
                    value={compFilter}
                    onChange={e => setCompFilter(e.target.value)}
                >
                    <option value="">Todas las empresas</option>
                    {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" style={{ color:'var(--sa-primary)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="sa-empty">
                        <FontAwesomeIcon icon={faUsers} style={{ fontSize:'3rem', opacity:0.15, display:'block', margin:'0 auto 1rem' }} />
                        <p>No se encontraron usuarios</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Usuario</th>
                                    <th>DNI</th>
                                    <th>Empresa</th>
                                    <th>Sucursal</th>
                                    <th>Rol</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(s => (
                                    <tr key={s.UsrId}>
                                        <td>
                                            <div style={{ fontWeight:600, fontSize:'0.9rem' }}>
                                                {s.UsrNombre} {s.UsrApellido}
                                            </div>
                                        </td>
                                        <td style={{ color:'#64748b', fontSize:'0.85rem' }}>{s.UsrDNI}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1" style={{ color:'#94a3b8', fontSize:'0.82rem' }}>
                                                <FontAwesomeIcon icon={faBuilding} style={{ opacity:0.4, fontSize:'0.75rem' }} />
                                                {s.EmpresaNombre}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1" style={{ color:'#94a3b8', fontSize:'0.82rem' }}>
                                                <FontAwesomeIcon icon={faStore} style={{ opacity:0.4, fontSize:'0.75rem' }} />
                                                {s.SucursalNombre}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="sa-badge sa-badge-premium" style={{ fontSize:'0.65rem' }}>
                                                {s.UsrRol || 'Staff'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`sa-badge ${s.UsrActivo ? 'sa-badge-success' : 'sa-badge-danger'}`}>
                                                <FontAwesomeIcon icon={s.UsrActivo ? faCheckCircle : faBan} />
                                                {s.UsrActivo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="sa-btn sa-btn-ghost sa-btn-sm"
                                                onClick={() => openEdit(s)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} /> Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <Modal show={!!modal} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight:800, fontSize:'1.1rem', color:'#f8fafc' }}>
                        <FontAwesomeIcon icon={faEdit} style={{ color:'var(--sa-primary)', marginRight:'0.5rem' }} />
                        Editar — {modal?.UsrNombre} {modal?.UsrApellido}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-4">
                        {/* Status toggle */}
                        <div>
                            <label className="sa-form-label">Estado del usuario</label>
                            <div className="d-flex gap-2">
                                <button
                                    className={`sa-btn ${editData.UsrActivo ? 'sa-btn-success' : 'sa-btn-ghost'}`}
                                    style={{ flex:1 }}
                                    onClick={() => setEditData(d => ({ ...d, UsrActivo: 1 }))}
                                >
                                    <FontAwesomeIcon icon={faCheckCircle} /> Activo
                                </button>
                                <button
                                    className={`sa-btn ${!editData.UsrActivo ? 'sa-btn-danger' : 'sa-btn-ghost'}`}
                                    style={{ flex:1 }}
                                    onClick={() => setEditData(d => ({ ...d, UsrActivo: 0 }))}
                                >
                                    <FontAwesomeIcon icon={faBan} /> Inactivo
                                </button>
                            </div>
                        </div>

                        {/* New password */}
                        <div>
                            <label className="sa-form-label">Nueva contraseña (opcional)</label>
                            <input
                                type="password"
                                className="sa-input"
                                placeholder="Dejar vacío para no cambiar"
                                value={editData.newPassword || ''}
                                onChange={e => setEditData(d => ({ ...d, newPassword: e.target.value }))}
                            />
                            <small style={{ color:'#475569', fontSize:'0.72rem', marginTop:4, display:'block' }}>
                                Solo completar si se desea resetear la contraseña del usuario.
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}>
                        <FontAwesomeIcon icon={faTimes} /> Cancelar
                    </button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : (
                            <><FontAwesomeIcon icon={faSave} /> Guardar cambios</>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SuperAdminStaff;
